import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type {
  EchoflicksSessionJourney,
  EchoflicksSessionMedia,
} from "../types";
import { IdentityReferenceSceneBadge } from "./IdentityReferenceBadge";
import {
  buildIdentityReferenceScenes,
  type IdentityReferenceScene,
} from "./identityReferences";
import { EmptyHint } from "./EchoflicksSessionList";
import {
  buildI2VDiagnostics,
  type I2VSceneDiagnostics,
} from "./i2vDiagnostics";

/** Self-hosted LTX-2.5: provider `ltx25_comfy`, model `ltx-2.5-22b-distilled`. */
const LTX25_LABEL = "LTX-2.5 (self-hosted)";

function isLtx25(value: string | null) {
  return Boolean(
    value && (value.includes("ltx25") || value.includes("ltx-2.5")),
  );
}

function friendlyModel(value: string | null, provider: string | null = null) {
  if (isLtx25(value) || (!value && isLtx25(provider))) return LTX25_LABEL;
  if (!value) return "Model pending";
  if (value.includes("veo-3.1-fast")) return "Veo 3.1 Fast";
  if (value.includes("veo-3.1")) return "Veo 3.1";
  if (value.includes("kling")) return "Kling";
  if (value.includes("ltx")) return "LTX";
  if (value.includes("seedance")) return "Seedance";
  return value;
}

function sceneStatusLabel(scene: I2VSceneDiagnostics) {
  if (scene.status === "fallback") return "Completed with fallback";
  if (scene.status === "retried") return "Accepted after retry";
  if (scene.status === "accepted") return "Accepted";
  if (scene.status === "missing") return "Prompt missing";
  if (scene.status === "static") return "Awaiting animation";
  return "In progress";
}

function statusVariant(scene: I2VSceneDiagnostics) {
  if (scene.status === "missing") return "destructive" as const;
  if (scene.status === "fallback" || scene.status === "retried")
    return "secondary" as const;
  return "outline" as const;
}

function SceneCard({
  scene,
  showTechnical,
  identityReferences,
}: {
  scene: I2VSceneDiagnostics;
  showTechnical: boolean;
  identityReferences?: IdentityReferenceScene;
}) {
  const authoring = scene.activeAuthoring;
  return (
    <article
      className="rounded-lg border bg-card p-3"
      aria-label={`Scene ${scene.sceneId}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-medium">
            Scene {scene.sceneId}
            {scene.sourceFile ? (
              <span className="ml-2 font-normal text-muted-foreground">
                {scene.sourceFile}
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            {scene.whyMotion ??
              "The director's motion explanation is not available yet."}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <IdentityReferenceSceneBadge scene={identityReferences} />
          <Badge variant="outline">
            {scene.routeRole === "primary"
              ? "Primary"
              : scene.routeRole === "secondary"
                ? "Secondary"
                : "Route unknown"}
          </Badge>
          {authoring?.status === "copied_legacy" ? (
            <Badge variant="outline">Scriptwriter prompt</Badge>
          ) : null}
          {scene.missingAuthoringAuditIds.length ? (
            <Badge variant="destructive">Authoring projection missing</Badge>
          ) : null}
          {scene.reconciledAuthoringAuditIds.length ? (
            <Badge variant="destructive">Authoring event missing</Badge>
          ) : null}
          <Badge variant={statusVariant(scene)}>
            {sceneStatusLabel(scene)}
          </Badge>
          <Badge variant="outline">
            {friendlyModel(scene.finalModel, scene.finalProvider)}
          </Badge>
        </div>
      </div>

      <div
        className={`mt-3 grid gap-2 text-sm ${showTechnical ? "sm:grid-cols-2" : ""}`}
      >
        <details className="rounded-md bg-muted/40 p-2">
          <summary className="cursor-pointer font-medium">
            Why this motion?
          </summary>
          <div className="mt-2 space-y-1 text-muted-foreground">
            <p>
              {scene.whyMotion ??
                "No operator-facing explanation was recorded."}
            </p>
            {authoring?.decision ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-xs">
                {Object.entries(authoring.decision).map(([key, value]) => (
                  <div className="contents" key={key}>
                    <dt className="font-medium text-foreground">
                      {key.replaceAll("_", " ")}
                    </dt>
                    <dd>
                      {Array.isArray(value)
                        ? value.join(", ")
                        : String(value ?? "")}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>
        </details>
        {showTechnical ? (
          <details className="rounded-md bg-muted/40 p-2">
            <summary className="cursor-pointer font-medium">
              Show prompt
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words text-xs">
              {scene.authoredPrompt ??
                "Prompt text was redacted or unavailable."}
            </pre>
          </details>
        ) : null}
      </div>

      {showTechnical ? (
        <details className="mt-2 text-xs text-muted-foreground">
          <summary className="cursor-pointer">Technical correlation</summary>
          <dl className="mt-2 grid gap-1 sm:grid-cols-2">
            <div className="contents">
              <dt>Authoring</dt>
              <dd>{authoring?.audit_id ?? "unavailable"}</dd>
            </div>
            <div className="contents">
              <dt>Target</dt>
              <dd>{authoring?.target_key ?? "unknown"}</dd>
            </div>
            <div className="contents">
              <dt>Guide</dt>
              <dd>{authoring?.guide_version ?? "unknown"}</dd>
            </div>
            <div className="contents">
              <dt>Correlation</dt>
              <dd>{scene.correlation ?? "not attempted"}</dd>
            </div>
            <div className="contents">
              <dt>Provider attempts</dt>
              <dd>{scene.providerAttemptCount}</dd>
            </div>
            <div className="contents">
              <dt>Final provider</dt>
              <dd>
                {isLtx25(scene.finalProvider)
                  ? `${LTX25_LABEL} · ${scene.finalProvider}`
                  : (scene.finalProvider ?? "pending")}
              </dd>
            </div>
            <div className="contents">
              <dt>Scene revision</dt>
              <dd>{scene.sceneRevisionId ?? "unavailable"}</dd>
            </div>
            <div className="contents">
              <dt>Prompt revision</dt>
              <dd>{scene.promptRevisionId ?? "unavailable"}</dd>
            </div>
            <div className="contents">
              <dt>Config release</dt>
              <dd>
                {scene.configReleaseId ?? "unavailable"} /{" "}
                {scene.configReleaseDigest ?? "digest unavailable"}
              </dd>
            </div>
            <div className="contents">
              <dt>Route contract</dt>
              <dd>
                {scene.routeContractVersion ?? "unavailable"} /{" "}
                {scene.routeContractDigest ?? "digest unavailable"}
              </dd>
            </div>
            <div className="contents">
              <dt>Prompt bundle</dt>
              <dd>
                {scene.promptBundleId ?? "unavailable"} /{" "}
                {scene.promptBundleDigest ?? "digest unavailable"}
              </dd>
            </div>
            <div className="contents">
              <dt>Template</dt>
              <dd>
                {scene.templateId ?? "unavailable"} /{" "}
                {scene.templateVersion ?? "version unavailable"} /{" "}
                {scene.templateDigest ?? "digest unavailable"}
              </dd>
            </div>
            <div className="contents">
              <dt>Packet</dt>
              <dd>{scene.packetId ?? "unavailable"}</dd>
            </div>
            <div className="contents">
              <dt>Packet digest</dt>
              <dd>{scene.packetDigest ?? "unavailable"}</dd>
            </div>
            <div className="contents">
              <dt>Provider request</dt>
              <dd>{scene.providerRequestDigest ?? "unavailable"}</dd>
            </div>
            <div className="contents">
              <dt>Render fingerprint</dt>
              <dd>{scene.renderSpecFingerprint ?? "unavailable"}</dd>
            </div>
            <div className="contents">
              <dt>Requested / reported</dt>
              <dd>
                {scene.requestedModel ?? "unknown"} /{" "}
                {scene.reportedModel ?? "unknown"}
                {scene.modelMismatch ? " (mismatch)" : ""}
              </dd>
            </div>
            <div className="contents">
              <dt>Retry reason</dt>
              <dd>{scene.retryReason ?? "none"}</dd>
            </div>
            <div className="contents">
              <dt>Fallback from</dt>
              <dd>{scene.fallbackReason ?? "none"}</dd>
            </div>
            <div className="contents">
              <dt>Reuse</dt>
              <dd>
                {scene.reuseStatus ?? "not evaluated"}
                {scene.reuseReason ? ` — ${scene.reuseReason}` : ""}
              </dd>
            </div>
          </dl>
          {scene.missingAuthoringAuditIds.length ? (
            <div className="mt-2 text-destructive">
              Provider attempts reference missing authoring rows:{" "}
              {scene.missingAuthoringAuditIds.join(", ")}
            </div>
          ) : null}
          {scene.reconciledAuthoringAuditIds.length ? (
            <div className="mt-2 text-destructive">
              Correlation was reconstructed from provider attempts; the source
              authoring event is still absent:{" "}
              {scene.reconciledAuthoringAuditIds.join(", ")}
            </div>
          ) : null}
          {scene.attemptLineage.length ? (
            <ol
              className="mt-3 space-y-1"
              aria-label="Provider attempt lineage"
            >
              {scene.attemptLineage.map((attempt) => (
                <li
                  className="rounded border bg-muted/30 p-2"
                  key={`${attempt.auditId}:${attempt.semanticAttemptOrdinal ?? "na"}:${attempt.transportTryIndex ?? "na"}`}
                >
                  {attempt.status} · {attempt.provider ?? "provider unknown"} ·
                  semantic {attempt.semanticAttemptOrdinal ?? "?"} / transport{" "}
                  {attempt.transportTryIndex ?? "?"}
                  {attempt.parentAttemptId
                    ? ` · parent ${attempt.parentAttemptId}`
                    : ""}
                  {attempt.patchId ? ` · patch ${attempt.patchId}` : ""}
                  {attempt.idempotencyKey
                    ? ` · idempotency ${attempt.idempotencyKey}`
                    : ""}
                  {attempt.correctionValidation?.status ===
                  "discarded_reroll" ? (
                    <div className="mt-1 text-amber-700 dark:text-amber-300">
                      Correction failed validation and was discarded. H3 reroll{" "}
                      {attempt.correctionValidation.semantic_attempt_ordinal ??
                        "?"}{" "}
                      was prepared without submitting the invalid prompt.
                      {attempt.correctionValidation.reason_code
                        ? ` (${attempt.correctionValidation.reason_code})`
                        : ""}
                    </div>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : null}
          {scene.transformationRecords.length ? (
            <pre className="mt-2 max-h-36 overflow-auto whitespace-pre-wrap rounded bg-muted/30 p-2">
              {JSON.stringify(scene.transformationRecords, null, 2)}
            </pre>
          ) : null}
          {scene.capabilityWarnings.length ? (
            <div className="mt-2 text-amber-700 dark:text-amber-300">
              Capability warnings: {JSON.stringify(scene.capabilityWarnings)}
            </div>
          ) : null}
        </details>
      ) : null}
    </article>
  );
}

export function I2VDirectorSummary({
  journey,
  isLoading = false,
  showTechnical = false,
  envelope,
  media,
}: {
  journey?: EchoflicksSessionJourney;
  isLoading?: boolean;
  showTechnical?: boolean;
  /** Delivery envelope + session media: only needed for the multi-ref badges. */
  envelope?: Record<string, unknown>;
  media?: EchoflicksSessionMedia[];
}) {
  const model = buildI2VDiagnostics(journey);
  const identityScenes = buildIdentityReferenceScenes(envelope, media ?? []);
  const identityByScene = new Map(
    identityScenes
      .filter((scene) => scene.sceneKey != null)
      .map((scene) => [scene.sceneKey as string, scene]),
  );
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cinematic animation</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyHint>Loading per-photo direction…</EmptyHint>
        </CardContent>
      </Card>
    );
  }
  if (
    model.telemetryState === "unavailable_legacy" &&
    model.scenes.length === 0
  ) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cinematic animation</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyHint>
            Per-photo prompt direction was not recorded for this legacy Echo.
            The video itself is unaffected.
          </EmptyHint>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Cinematic animation</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {model.authoringMode === "legacy_script"
                ? "How each photo became a moving shot. Prompts were written by the scriptwriter with the full film in view (per-model director off)."
                : "How the director turned each photo into a coherent moving shot."}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {model.authoringMode === "legacy_script" ? (
              <Badge variant="outline">Scriptwriter prompts</Badge>
            ) : null}
            <Badge
              variant={model.status === "complete" ? "secondary" : "outline"}
            >
              {model.status === "complete" ? "Complete" : "Partial visibility"}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {model.authoringProjectionGapCount ? (
          <div
            className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            role="alert"
          >
            {model.authoringProjectionGapCount} provider-attempt authoring{" "}
            {model.authoringProjectionGapCount === 1
              ? "projection is"
              : "projections are"}{" "}
            incomplete. Provider attempts remain visible for audit and
            reconciliation.
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">
            {model.directedSceneCount}/{model.eligibleSceneCount} photos
            directed
          </Badge>
          <Badge variant="outline">
            {model.primarySceneCount} Primary / {model.secondarySceneCount}{" "}
            Secondary
          </Badge>
          <Badge variant="outline">
            {model.customPromptSceneCount} custom prompts
          </Badge>
          <Badge variant="outline">{model.acceptedSceneCount} animated</Badge>
          {model.retriedSceneCount ? (
            <Badge variant="secondary">
              {model.retriedSceneCount} retried scenes
            </Badge>
          ) : null}
          {model.fallbackSceneCount ? (
            <Badge variant="secondary">
              {model.fallbackSceneCount} fallback scenes
            </Badge>
          ) : null}
          <Badge variant="outline">
            {model.providerAttemptCount} provider{" "}
            {model.providerAttemptCount === 1 ? "attempt" : "attempts"}
          </Badge>
        </div>

        <details className="rounded-lg border p-3">
          <summary className="cursor-pointer font-medium">
            How the film stays coherent
          </summary>
          <p className="mt-2 text-sm text-muted-foreground">
            Every photo is directed with its position and neighbouring shots in
            view. Shared vision and shot-list fingerprints let operators confirm
            that all scene decisions came from the same film context.
          </p>
          <div className="mt-2 grid gap-1 font-mono text-xs text-muted-foreground sm:grid-cols-2">
            <span>Vision: {model.visionDigest ?? "unavailable"}</span>
            <span>Shot list: {model.shotListDigest ?? "unavailable"}</span>
          </div>
        </details>

        <div className="grid gap-3 xl:grid-cols-2">
          {model.scenes.map((scene) => (
            <SceneCard
              key={scene.sceneId}
              scene={scene}
              showTechnical={showTechnical}
              identityReferences={identityByScene.get(String(scene.sceneId))}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
